import config from '../config'
import { fetcher, useFetchWithAuth } from './utils'
import { captureMessage } from '@sentry/react'
import { AuthorisePaymentRequest, AuthorisePaymentResponse, PaymentStatusResponse } from '../../generated-api'
import { useAuth } from 'context/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from 'api'
import { parseError } from 'utils/error-handler'
import { ClientPaymentDto } from '../../generated-api/models/ClientPaymentDto'

const BASE = config.basePath
const BASE_PUBLIC_OPEN = `${BASE}/v1/public-open`
const BASE_CLIENT_API = `${BASE}/v1/client`

export const getPaymentStatus = async (
    paymentReference: string | null,
    redirectResult: string | null
): Promise<PaymentStatusResponse> => {
    if (!paymentReference) throw Error('Payment reference is undefined')

    const redirectParam = redirectResult ? `?redirectResult=${redirectResult}` : ''

    return fetcher<undefined, PaymentStatusResponse>(
        `${BASE_PUBLIC_OPEN}/payments/${paymentReference}/status${redirectParam}`
    )
}

export const cancelPayment = async (paymentReference: string): Promise<void> => {
    return fetcher(`${BASE_PUBLIC_OPEN}/payments/${paymentReference}/cancel`, 'POST')
}

export const cancelPaymentWithBeacon = async (paymentReference: string): Promise<void> => {
    // sendBeacon returns true if the user agent successfully queued the data for transfer. Otherwise, it returns false.
    const result = navigator.sendBeacon(`${BASE_PUBLIC_OPEN}/payments/${paymentReference}/cancel`)

    if (!result) {
        captureMessage(`sendBeacon returned false when cancelling payment ${paymentReference}`, 'error')
    }
}

export const authorisePayment = async (
    paymentReference: string,
    authorisePaymentRequest: AuthorisePaymentRequest
): Promise<AuthorisePaymentResponse> => {
    return fetcher(`${BASE_PUBLIC_OPEN}/payments/${paymentReference}/authorise`, 'POST', authorisePaymentRequest)
}

export const useGetPayment = (paymentReference: string) => {
    const { getAccessToken } = useAuth()
    return useQuery(['getPayment', paymentReference], async () =>
        apiClient.PaymentControllerApi.getPayment1(
            { paymentReference: paymentReference },
            { headers: { Authorization: `Bearer ${await getAccessToken()}` } }
        ).catch(parseError)
    )
}

const getDefaultPagingParams = (pageNo: number): URLSearchParams => {
    return new URLSearchParams({
        pageNo: pageNo.toString(),
        pageSize: '50',
        total: 'false',
        sortBy: 'created',
        direction: 'DESC',
    })
}

export const useGetPayments = () => {
    const fetchWithAuth = useFetchWithAuth()
    return async (pageNo = 0): Promise<ClientPaymentDto[]> => {
        const params = getDefaultPagingParams(pageNo)
        return fetchWithAuth(`${BASE_CLIENT_API}/payments?${params}`)
    }
}

export const useGetPaymentEvents = (paymentReference: string) => {
    const { getAccessToken } = useAuth()
    return useQuery(['getPaymentEvents', paymentReference], async () =>
        apiClient.PaymentControllerApi.getPaymentEvents(
            { paymentReference: paymentReference },
            { headers: { Authorization: `Bearer ${await getAccessToken()}` } }
        ).catch(parseError)
    )
}

export const useSearchPayments = () => {
    const fetchWithAuth = useFetchWithAuth()
    const getPayments = useGetPayments()
    return async (query: string | undefined, pageNo: number) => {
        if (!query) return getPayments(pageNo)

        const params = getDefaultPagingParams(pageNo)
        params.append('query', query)
        return fetchWithAuth<undefined, ClientPaymentDto[]>(`${BASE_CLIENT_API}/payments/search?${params}`)
    }
}
